import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API, fmtDate, fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, Sun, Leaf, CheckCircle2 } from "lucide-react";
import WhatsAppShareButton from "@/components/crm/WhatsAppShareButton";

const Section = ({ title, children }) => (
  <div className="mb-6">
    <div className="flex items-center gap-3 mb-4">
      <div className="text-base font-extrabold tracking-widest text-slate-900 uppercase" style={{fontFamily:'Outfit'}}>{title}</div>
      <div className="flex-1 h-px bg-amber-500" />
    </div>
    {children}
  </div>
);

const Page = ({ children, footer }) => (
  <div className="print-page bg-white p-8 sm:p-12 shadow-lg mb-6 relative min-h-[280mm] flex flex-col">
    <div className="flex-1">{children}</div>
    <div className="pt-4 mt-6 border-t border-slate-200 text-[10px] text-slate-500 flex justify-between">
      <span>{footer?.left}</span>
      <span className="text-slate-400">{footer?.right}</span>
    </div>
  </div>
);

const CompanyStrip = ({ co }) => (
  <div className="flex items-center gap-3 mb-3">
    {co?.logo_url ? (
      <img src={co.logo_url} alt="MRM" className="h-10 object-contain" />
    ) : (
      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center"><Sun className="w-5 h-5 text-white" strokeWidth={2.5} /></div>
    )}
    <div>
      <div className="text-sm font-bold text-slate-900" style={{fontFamily:'Outfit'}}>{co?.company_name || "MRM RENEWABLES ENERGY PRIVATE LIMITED"}</div>
      <div className="text-[10px] text-slate-500">GSTIN: {co?.gstin || "-"} · Tel: {co?.phone || "-"}</div>
    </div>
  </div>
);

export default function QuotationPrint() {
  const { id } = useParams();
  const nav = useNavigate();
  const [q, setQ] = useState(null);
  const [co, setCo] = useState(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("mrm_token") : null;

  useEffect(() => {
    api.get(`/quotations/${id}`).then(({ data }) => setQ(data));
    api.get("/settings/company").then(({ data }) => setCo(data)).catch(() => {});
  }, [id]);

  if (!q) return <div className="p-10 text-center text-slate-400">Loading...</div>;

  const kw = q.system_size_kw || 0;
  const unitsDay = q.units_per_day ?? Math.round(kw * 4.31 * 10) / 10;
  const yearlyUnits = q.yearly_units ?? Math.round(kw * 4.31 * 365);
  const rate = q.electricity_rate || 8;
  const yearlySavings = q.yearly_savings ?? Math.round(yearlyUnits * rate);
  const systemCost = q.system_cost ?? q.subtotal ?? 0;
  const gstAmt = q.gst_amount ?? Math.round(systemCost * (q.gst_percent || 8.9) / 100);
  const finalCost = q.total ?? (systemCost + gstAmt - (q.subsidy || 0) - (q.discount || 0));
  const payback = q.payback_years ?? (yearlySavings ? (finalCost / yearlySavings).toFixed(1) : 0);
  const lifetime = q.lifetime_savings ?? yearlySavings * 25;
  const co2 = q.co2_offset_tons ?? Math.round(yearlyUnits * 0.00082 * 25 * 10) / 10;
  const forest = q.forest_acres ?? Math.round(co2 / 21.3 * 100) / 100;
  const coal = q.coal_avoided_tons ?? Math.round(yearlyUnits * 0.0005 * 25 * 100) / 100;

  const costRow = (label, val, opts = {}) => (
    <tr className={`border-b border-slate-200 ${opts.cls || ""}`}>
      <td className={`py-3 text-sm ${opts.bold ? "font-bold text-slate-900" : "text-slate-700"}`}>{label}</td>
      <td className={`py-3 text-sm text-right font-mono ${opts.bold ? "font-bold text-slate-900" : ""} ${opts.cls || ""}`}>{val}</td>
    </tr>
  );

  const payStep = (pct, label) => (
    <div key={label} className="flex-1 text-center">
      <div className="w-14 h-14 mx-auto rounded-full bg-amber-500 text-white flex items-center justify-center font-bold text-sm shadow-md shadow-amber-500/30">{pct}%</div>
      <div className="text-xs font-semibold text-slate-800 mt-2">{label}</div>
    </div>
  );

  const foot = (n) => ({ left: `${co?.company_name || "MRM Renewables"} · Proposal ${q.quotation_no}`, right: `Page ${n} of 5` });

  return (
    <div className="min-h-screen bg-slate-100 py-8">
      <div className="no-print max-w-4xl mx-auto flex justify-between items-center mb-4 px-4">
        <Button variant="outline" onClick={() => nav(-1)}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
        <div className="flex items-center gap-2">
          <WhatsAppShareButton
            mobile={q.customer_mobile}
            message={`Namaste ${q.customer_name || ""},\n\nYeh raha aapka Solar Proposal — ${kw} kW On-Grid System.\n\nProposal No.: ${q.quotation_no}\nFinal Cost: ${fmtINR(finalCost)}\nYearly Savings: ${fmtINR(yearlySavings)}\nPayback: ${payback} years\n\nHumari team se contact karne ke liye dhanyavaad.\n\n— MRM Renewables Energy Pvt Ltd`}
            label="Share on WhatsApp"
            variant="solid"
            size="default"
            testid="whatsapp-proposal-print"
          />
          <Button className="bg-amber-500 hover:bg-amber-600 text-white" onClick={() => window.print()} data-testid="print-btn"><Printer className="w-4 h-4 mr-1" />Print / Save PDF</Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto" data-testid="quotation-print-doc">

        {/* ============= PAGE 1 — COVER ============= */}
        <Page footer={foot(1)}>
          <div className="text-center text-sm font-bold tracking-[0.3em] text-slate-700 border-b border-slate-300 pb-2 mb-6">TAX INVOICE / SOLAR PROPOSAL</div>

          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 border-b-4 border-amber-500 pb-5 mb-8">
            <div className="flex items-start gap-4">
              {co?.logo_url ? (
                <img src={co.logo_url} alt="MRM" className="h-20 object-contain" data-testid="print-logo" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center"><Sun className="w-9 h-9 text-white" strokeWidth={2.5} /></div>
              )}
              <div>
                <div className="text-xl font-extrabold text-slate-900 leading-tight" style={{fontFamily:'Outfit'}}>{co?.company_name || "MRM RENEWABLES ENERGY PRIVATE LIMITED"}</div>
                {co?.address && <div className="text-[11px] text-slate-600 max-w-md mt-1 leading-snug">{co.address}</div>}
                <div className="text-[11px] text-slate-600 mt-1">Tel: {co?.phone || "-"} · Email: {co?.email || "-"}</div>
                <div className="text-[11px] font-semibold text-slate-800 mt-1">
                  GSTIN: {co?.gstin || "-"}{co?.cin && <span className="font-normal text-slate-600"> · CIN: {co.cin}</span>}{co?.pan && <span className="font-normal text-slate-600"> · PAN: {co.pan}</span>}
                </div>
                {co?.udyam && <div className="text-[11px] text-slate-600">Udyam No.: {co.udyam}</div>}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-xs uppercase tracking-wider text-slate-500">Proposal No.</div>
              <div className="text-2xl font-bold font-mono text-slate-900">{q.quotation_no}</div>
              <div className="text-xs text-slate-500 mt-1">Date: {fmtDate(q.created_at)}</div>
            </div>
          </div>

          <div className="text-center py-6">
            <div className="text-[11px] uppercase tracking-[0.4em] text-amber-600 font-semibold">Solar Proposal</div>
            <div className="text-5xl font-extrabold text-slate-900 mt-3" style={{fontFamily:'Outfit'}}>{kw} kW On-Grid</div>
            <div className="text-sm text-slate-500 mt-1">Rooftop Solar Installation</div>
          </div>

          <div className="grid grid-cols-2 gap-6 mt-6">
            <div className="border border-slate-200 rounded-xl p-5 bg-slate-50/60">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Prepared For</div>
              <div className="text-lg font-bold text-slate-900">{q.customer_name}</div>
              <div className="text-sm text-slate-600 mt-0.5">{q.customer_mobile}</div>
              {q.customer_address && <div className="text-sm text-slate-600">{q.customer_address}</div>}
              {q.customer_city && <div className="text-sm text-slate-600">{q.customer_city}</div>}
            </div>
            <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-5 text-white shadow-lg shadow-amber-500/30">
              <div className="text-xs uppercase tracking-widest text-amber-100 font-semibold">System Details</div>
              <div className="text-3xl font-extrabold mt-1" style={{fontFamily:'Outfit'}}>{kw} kW On-Grid</div>
              <div className="text-xs text-amber-50 mt-2">{q.panel_brand} {q.panel_type || ""} · {q.panel_wattage}</div>
              <div className="text-xs text-amber-50">{q.inverter_brand} · {q.inverter_capacity}</div>
              <div className="text-xs text-amber-50">Structure: {q.structure_type || "HDGI"}</div>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-3 text-center">
            <div className="border border-slate-200 rounded-xl p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Total Capacity Installed</div>
              <div className="text-2xl font-extrabold text-slate-900 mt-1 font-mono">100+</div>
            </div>
            <div className="border border-slate-200 rounded-xl p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Happy Customers</div>
              <div className="text-2xl font-extrabold text-slate-900 mt-1 font-mono">30+</div>
            </div>
            <div className="border border-slate-200 rounded-xl p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Cities Served</div>
              <div className="text-2xl font-extrabold text-slate-900 mt-1 font-mono">20+</div>
            </div>
          </div>

          <div className="mt-6 text-center text-sm text-slate-600 max-w-2xl mx-auto">
            We are on a mission to deliver world-class solar installations ensuring maximum performance, durability, and ROI for every project. Every site is installed end-to-end with 5 years of AMC & monitoring.
          </div>

          <div className="mt-8 text-xs text-slate-500 text-center">Prepared by: <span className="font-semibold text-slate-800">{q.created_by_name || "MRM Team"}</span></div>
        </Page>

        {/* ============= PAGE 2 — GENERATION + ROI ============= */}
        <Page footer={foot(2)}>
          <CompanyStrip co={co} />

          <Section title="Generation Estimate">
            <div className="grid grid-cols-4 gap-3 text-center" data-testid="generation-section">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <div className="text-2xl font-extrabold text-emerald-700 font-mono">{unitsDay}</div>
                <div className="text-[11px] uppercase tracking-wider text-emerald-800 font-semibold mt-1">Units / Day</div>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <div className="text-2xl font-extrabold text-emerald-700 font-mono">{yearlyUnits.toLocaleString("en-IN")}</div>
                <div className="text-[11px] uppercase tracking-wider text-emerald-800 font-semibold mt-1">Units / Year</div>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <div className="text-2xl font-extrabold text-slate-800 font-mono">80%</div>
                <div className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold mt-1">Performance Ratio</div>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <div className="text-2xl font-extrabold text-slate-800 font-mono">13.8%</div>
                <div className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold mt-1">Monsoon Dip</div>
              </div>
            </div>
            <div className="text-[10px] text-slate-400 mt-2">* Indicative at 4.31 units/kW/day (Indian avg). Actual generation may vary with site conditions, shading & weather patterns.</div>

            <div className="mt-6 border border-slate-200 rounded-xl p-5" data-testid="monthly-chart">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Round-the-Year Generation Pattern</div>
                {q.monthly_generation && (
                  <div className="text-[10px] text-slate-500">
                    City: <span className="font-semibold text-slate-800">{q.monthly_generation.city_used}</span>
                    <span className="mx-1.5">·</span>
                    Tilt: <span className="font-semibold text-slate-800">{q.monthly_generation.tilt_used_deg}°</span>
                    <span className="text-slate-400"> (Optimal {q.monthly_generation.optimal_tilt_deg}°)</span>
                  </div>
                )}
              </div>
              {(() => {
                const months = q.monthly_generation?.months || ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m,i)=>({month:m, units:[85,90,105,115,120,95,75,80,95,100,90,80][i]*10}));
                const max = Math.max(1, ...months.map(x=>x.units));
                return (
                  <>
                    <div className="grid grid-cols-12 gap-1.5 items-end h-40">
                      {months.map((m,i)=>{
                        const h = Math.round((m.units / max) * 130);
                        const isPeak = m.units === max;
                        const label = m.units >= 1000 ? (m.units/1000).toFixed(1)+"k" : String(m.units);
                        return (
                          <div key={m.month} className="flex flex-col items-center h-full justify-end" title={`${m.month}: ${m.units.toLocaleString('en-IN')} kWh`}>
                            <div className={`text-[8px] font-mono mb-0.5 ${isPeak?"text-amber-700 font-bold":"text-slate-600"}`}>{label}</div>
                            <div className={`w-full rounded-t shadow-sm ${isPeak?"bg-gradient-to-t from-amber-700 via-amber-500 to-amber-300":"bg-gradient-to-t from-amber-600 via-amber-500 to-amber-300"}`} style={{height:`${h}px`}}/>
                            <div className="text-[9px] text-slate-500 mt-1">{m.month}</div>
                          </div>
                        );
                      })}
                    </div>
                    {q.monthly_generation && (
                      <div className="text-[10px] text-slate-500 mt-3 flex items-center justify-between border-t border-slate-100 pt-2 flex-wrap gap-2">
                        <span>Annual Estimate: <span className="font-semibold text-slate-800 font-mono">{q.monthly_generation.yearly_total.toLocaleString("en-IN")} kWh</span></span>
                        <span>System efficiency: {Math.round(q.monthly_generation.system_efficiency*100)}% · Tilt fit: {q.monthly_generation.tilt_efficiency}%</span>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </Section>

          <Section title="Return on Investment">
            <div className="text-sm font-semibold text-slate-700 mb-3">Solar is one of the best investments you will ever make.</div>
            <div className="grid grid-cols-3 gap-3 text-center" data-testid="roi-section">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="text-2xl font-extrabold text-amber-700 font-mono">{fmtINR(yearlySavings)}</div>
                <div className="text-[11px] uppercase tracking-wider text-amber-800 font-semibold mt-1">Yearly Savings</div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="text-2xl font-extrabold text-amber-700 font-mono">{payback} yrs</div>
                <div className="text-[11px] uppercase tracking-wider text-amber-800 font-semibold mt-1">Payback Period</div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="text-2xl font-extrabold text-amber-700 font-mono">{fmtINR(lifetime)}</div>
                <div className="text-[11px] uppercase tracking-wider text-amber-800 font-semibold mt-1">25-Yr Lifetime Savings</div>
              </div>
            </div>
            <div className="text-[10px] text-slate-400 mt-2">* Calculated at ₹{rate}/unit electricity tariff. Panel degradation & inflation adjusted for indicative use.</div>
            <div className="mt-5 bg-slate-900 text-white rounded-xl p-4 text-sm">
              💡 Easy Financing Options Available — Residential loans starting at 8% p.a.
            </div>
          </Section>
        </Page>

        {/* ============= PAGE 3 — COMPONENTS + COST ============= */}
        <Page footer={foot(3)}>
          <CompanyStrip co={co} />

          <Section title="Solar Components">
            <div className="text-sm text-slate-600 mb-3">High-quality components from trusted Tier-1 OEMs, selected for performance, safety, and long-term ROI.</div>
            <table className="w-full">
              <thead className="border-b-2 border-slate-800">
                <tr>
                  <th className="text-left py-2 text-xs uppercase tracking-wider text-slate-600">Component</th>
                  <th className="text-left py-2 text-xs uppercase tracking-wider text-slate-600">Specification</th>
                  <th className="text-left py-2 text-xs uppercase tracking-wider text-slate-600">Warranty</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-slate-200"><td className="py-3 font-semibold">{q.panel_brand} Panels</td><td className="py-3 text-slate-600">{q.panel_type || "Bifacial"} High Wattage · {q.panel_wattage}</td><td className="py-3 text-slate-600">10 yrs product · 25 yrs performance</td></tr>
                <tr className="border-b border-slate-200"><td className="py-3 font-semibold">{q.inverter_brand} Inverter</td><td className="py-3 text-slate-600">On-Grid · {q.inverter_capacity} · Anti-islanding protection</td><td className="py-3 text-slate-600">10 years</td></tr>
                <tr className="border-b border-slate-200"><td className="py-3 font-semibold">Structure</td><td className="py-3 text-slate-600">{q.structure_type || "HDGI"} mounting structure</td><td className="py-3 text-slate-600">Corrosion resistant</td></tr>
                <tr className="border-b border-slate-200"><td className="py-3 font-semibold">DC Wire</td><td className="py-3 text-slate-600">{q.dc_wire_brand || "Polycab"} · {q.dc_wire_size || "4 sq.mm"} · U/V resistant double insulated</td><td className="py-3 text-slate-600">30 years</td></tr>
                <tr className="border-b border-slate-200"><td className="py-3 font-semibold">Earthing Wire</td><td className="py-3 text-slate-600">{q.earthing_wire_brand || "Polycab"} · {q.earthing_wire_size || "10 sq.mm"}</td><td className="py-3 text-slate-600">30 years</td></tr>
                <tr className="border-b border-slate-200"><td className="py-3 font-semibold">Armoured Cable</td><td className="py-3 text-slate-600">{q.armoured_cable_brand || "Polycab"} · {q.armoured_cable_size || "2C x 4 sq.mm"}</td><td className="py-3 text-slate-600">30 years</td></tr>
                <tr className="border-b border-slate-200"><td className="py-3 font-semibold">Earthing Rod</td><td className="py-3 text-slate-600">{q.earthing_rod || "2m GI/Copper Bonded (x3)"}</td><td className="py-3 text-slate-600">Maintenance-free</td></tr>
                <tr className="border-b border-slate-200"><td className="py-3 font-semibold">Lightning Arrester</td><td className="py-3 text-slate-600">{q.lightning_arrester || "1 No. with down conductor"}</td><td className="py-3 text-slate-600">Standard</td></tr>
                <tr className="border-b border-slate-200"><td className="py-3 font-semibold">ACDB</td><td className="py-3 text-slate-600">{q.acdb_brand || "True Power"} · with MCB/SPD protection</td><td className="py-3 text-slate-600">Standard</td></tr>
                <tr className="border-b border-slate-200"><td className="py-3 font-semibold">DCDB</td><td className="py-3 text-slate-600">{q.dcdb_brand || "True Power"} · with fuse/SPD protection</td><td className="py-3 text-slate-600">Standard</td></tr>
                <tr className="border-b border-slate-200"><td className="py-3 font-semibold">Connectors & Electricals</td><td className="py-3 text-slate-600">MC4 Connectors · Lugs · Glands</td><td className="py-3 text-slate-600">Standard</td></tr>
                {(q.additional_items || []).map((it, i) => (
                  <tr key={`extra-${i}`} className="border-b border-slate-200">
                    <td className="py-3 font-semibold">{it.name || "Additional Item"}</td>
                    <td className="py-3 text-slate-600">{it.qty} {it.unit || "Nos"} × {fmtINR(it.rate)}</td>
                    <td className="py-3 text-slate-600">-</td>
                  </tr>
                ))}
                <tr><td className="py-3 font-semibold">Services (MRM)</td><td className="py-3 text-slate-600">End-to-end installation · Net metering · Generation tracking</td><td className="py-3 text-slate-600">5 years free AMC</td></tr>
              </tbody>
            </table>
          </Section>

          <Section title="Offer & Cost">
            <table className="w-full" data-testid="cost-table">
              <thead className="border-b-2 border-slate-800">
                <tr><th className="text-left py-2 text-xs uppercase tracking-wider text-slate-600">Description</th><th className="text-right py-2 text-xs uppercase tracking-wider text-slate-600">Amount</th></tr>
              </thead>
              <tbody>
                {costRow(`${kw} kW On-Grid Solar System (complete installation, inclusive of all components)`, fmtINR(systemCost))}
                {(q.additional_items || []).map((it, i) => costRow(
                  `${it.name} (${it.qty} ${it.unit || "Nos"} × ${fmtINR(it.rate)})`,
                  fmtINR((it.qty || 0) * (it.rate || 0))
                ))}
                {costRow(`GST @ ${q.gst_percent || 8.9}%`, fmtINR(gstAmt))}
                {costRow("Less: Govt Subsidy (PM Surya Ghar)", `- ${fmtINR(q.subsidy || 0)}`, { cls: "text-emerald-700" })}
                {(q.discount || 0) > 0 && costRow("Less: Discount", `- ${fmtINR(q.discount)}`, { cls: "text-emerald-700" })}
                <tr className="bg-slate-900 text-white">
                  <td className="py-3.5 px-3 font-bold">FINAL COST</td>
                  <td className="py-3.5 px-3 text-right font-mono text-xl font-bold">{fmtINR(finalCost)}</td>
                </tr>
              </tbody>
            </table>
            <div className="text-[10px] text-slate-400 mt-2">* Inclusive of all taxes and installation. Subsidy subject to Govt approval under PM Surya Ghar scheme.</div>
          </Section>

          {(co?.bank_name || co?.account_no) && (
            <div className="border border-slate-200 rounded-xl p-4 bg-slate-50" data-testid="bank-details-block">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Bank Details for Payment</div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm text-slate-700">
                {co.bank_name && <div><span className="text-slate-500">Bank:</span> {co.bank_name}</div>}
                {co.branch && <div><span className="text-slate-500">Branch:</span> {co.branch}</div>}
                {co.account_no && <div><span className="text-slate-500">Account No:</span> <span className="font-mono">{co.account_no}</span></div>}
                {co.ifsc && <div><span className="text-slate-500">IFSC:</span> <span className="font-mono">{co.ifsc}</span></div>}
              </div>
            </div>
          )}
        </Page>

        {/* ============= PAGE 4 — PAYMENT + TIMELINE ============= */}
        <Page footer={foot(4)}>
          <CompanyStrip co={co} />

          <Section title="Payment Terms">
            <div className="flex items-start gap-2" data-testid="payment-terms">
              {payStep(30, "Advance")}
              <div className="flex-1 h-px bg-slate-300 mt-7" />
              {payStep(60, "Structure Complete")}
              <div className="flex-1 h-px bg-slate-300 mt-7" />
              {payStep(10, "Final / Net Metering")}
            </div>
            <div className="text-[11px] text-slate-500 mt-4 text-center">Net metering timeline is entirely dependent on DISCOM — we do not control that process.</div>
          </Section>

          {(q.loan_amount > 0) && (
            <Section title="Loan / EMI Option">
              <div className="text-sm text-slate-600 mb-3">Easy financing available — own your solar plant with affordable monthly EMIs.</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center" data-testid="loan-section">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="text-xl font-extrabold text-blue-700 font-mono">{fmtINR(q.loan_amount)}</div>
                  <div className="text-[11px] uppercase tracking-wider text-blue-800 font-semibold mt-1">Loan Amount</div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="text-xl font-extrabold text-blue-700 font-mono">{fmtINR(q.loan_emi)}/mo</div>
                  <div className="text-[11px] uppercase tracking-wider text-blue-800 font-semibold mt-1">Monthly EMI</div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="text-xl font-extrabold text-blue-700 font-mono">{q.loan_tenor_months} mo @ {q.loan_interest}%</div>
                  <div className="text-[11px] uppercase tracking-wider text-blue-800 font-semibold mt-1">Tenor · Interest</div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="text-xl font-extrabold text-blue-700 font-mono">{fmtINR(q.loan_total_interest)}</div>
                  <div className="text-[11px] uppercase tracking-wider text-blue-800 font-semibold mt-1">Total Interest</div>
                </div>
              </div>
              <div className="mt-3 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                Total loan payable over {q.loan_tenor_months} months: <span className="font-mono font-semibold">{fmtINR(q.loan_total_payable)}</span>. EMI subject to financier approval and prevailing rates.
              </div>
            </Section>
          )}

          <Section title="Project Timeline & Milestones">
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[
                ["01","Permitting","Permitting is completed within 1 week"],
                ["02","Procurement","Material procurement takes 1-2 weeks"],
                ["03","Site Assessment","Detailed site assessment in weeks 2-4"],
                ["04","Installation","On-site installation over 2-4 weeks"],
                ["05","Electrical","AC/DC electrical work by week 4"],
                ["06","Commissioning","Final commissioning by week 6"],
              ].map(([n, t, d]) => (
                <div key={n} className="flex gap-3 items-start bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="w-8 h-8 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center font-bold shrink-0">{n}</div>
                  <div><div className="font-semibold text-slate-800">{t}</div><div className="text-slate-500 text-xs">{d}</div></div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Scope of Services">
            <div className="grid grid-cols-2 gap-3">
              {["System Design & Engineering","End-to-end Installation","Structure Fabrication & Mounting","Electrical Wiring & Commissioning","Net Metering Application & Liaison","5 Years Free AMC","Generation Monitoring","Post-installation Support"].map(s => (
                <div key={s} className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5"/>
                  {s}
                </div>
              ))}
            </div>
          </Section>
        </Page>

        {/* ============= PAGE 5 — ENVIRONMENT + TERMS + SIGN ============= */}
        <Page footer={foot(5)}>
          <CompanyStrip co={co} />

          <Section title="Environment Impact">
            <div className="flex items-center gap-2 text-sm text-slate-600 mb-4"><Leaf className="w-4 h-4 text-emerald-600"/>You are contributing to solve earth's biggest problem — Climate Change.</div>
            <div className="grid grid-cols-3 gap-3 text-center" data-testid="environment-section">
              <div className="border border-emerald-200 bg-emerald-50/60 rounded-xl p-5">
                <div className="text-3xl font-extrabold text-emerald-700 font-mono">{co2}</div>
                <div className="text-[11px] uppercase tracking-wider text-emerald-800 font-semibold mt-2">Metric Tons</div>
                <div className="text-xs text-slate-600 mt-1">CO₂ Offset (25 yrs)</div>
              </div>
              <div className="border border-emerald-200 bg-emerald-50/60 rounded-xl p-5">
                <div className="text-3xl font-extrabold text-emerald-700 font-mono">{forest}</div>
                <div className="text-[11px] uppercase tracking-wider text-emerald-800 font-semibold mt-2">Acres / Year</div>
                <div className="text-xs text-slate-600 mt-1">Equivalent Forest Cover</div>
              </div>
              <div className="border border-emerald-200 bg-emerald-50/60 rounded-xl p-5">
                <div className="text-3xl font-extrabold text-emerald-700 font-mono">{coal}</div>
                <div className="text-[11px] uppercase tracking-wider text-emerald-800 font-semibold mt-2">Metric Tons</div>
                <div className="text-xs text-slate-600 mt-1">Coal Burn Avoided</div>
              </div>
            </div>
          </Section>

          <Section title="Terms & Conditions">
            <ul className="text-sm text-slate-700 space-y-2">
              {[
                "This proposal is valid for 15 days from the date of issue.",
                "All prices are in Indian Rupees (INR) and inclusive of GST as applicable.",
                "Subsidy under PM Surya Ghar is subject to Government approval and DISCOM processing.",
                "Net metering timelines depend on DISCOM and are not under our direct control.",
                q.warranty_text || "Panel warranty: 25 years performance · Inverter warranty: 10 years · Cables: 30 years.",
                "5 years free AMC with generation monitoring included with every installation.",
                "Any additional civil work / shifting of AC or DC line beyond scope will be quoted separately.",
                "In case of dispute, jurisdiction shall be limited to Jaipur, Rajasthan.",
              ].map(t => (
                <li key={t} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5"/>{t}</li>
              ))}
            </ul>
          </Section>

          {q.notes && <div className="mb-6 text-sm text-slate-600"><span className="font-semibold">Notes:</span> {q.notes}</div>}

          <div className="grid grid-cols-2 gap-6 mt-8 pt-6 border-t border-slate-200">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Customer Acceptance</div>
              <div className="text-xs text-slate-500">I hereby accept the above proposal along with the terms & conditions mentioned.</div>
              <div className="border-t border-slate-400 mt-12 pt-2 text-sm text-slate-800 font-semibold">Customer Signature & Date</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">For {co?.company_name || "MRM Renewables"}</div>
              {q.created_by_signature ? (
                <img src={`${API}/files/${q.created_by_signature}?auth=${token}`} alt="Signature" className="h-16 object-contain ml-auto" data-testid="proposal-sales-signature"/>
              ) : co?.logo_url ? (
                <img src={co.logo_url} alt="" className="h-16 object-contain ml-auto opacity-40"/>
              ) : <div className="h-16"/>}
              <div className="border-t border-slate-400 mt-2 pt-2 text-sm text-slate-800 font-semibold" data-testid="proposal-sales-name">{q.created_by_name || "Authorised Signatory"}</div>
              <div className="text-[10px] text-slate-500">Sales Representative</div>
            </div>
          </div>

          <div className="text-center mt-10 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-xl">
            <div className="text-lg font-bold" style={{fontFamily:'Outfit'}}>Thank You!</div>
            <div className="text-xs mt-1 text-amber-50">Looking forward to work with you and add value · {co?.phone} · {co?.email}</div>
          </div>
        </Page>

      </div>
    </div>
  );
}
